import { Box, Stack, Typography } from '@mui/material';
import { ReactNode } from 'react';

import { COLORS } from '@/styles/tokens/colors';

/** The size, in pixels, of the gray panel that holds the template art. */
export const TEMPLATE_PROMPT_CARD_ART_SIZE = { width: 80, height: 64 };

export interface TemplatePromptCardProps {
  description: string;
  /** Decorative art for the template. The card shows it in a fixed panel. */
  icon: ReactNode;
  title: string;
}

/**
 * The built-in template that a user chat message was sent with. Every
 * template uses this layout; only the icon and text change.
 */
export function TemplatePromptCard({
  description,
  icon,
  title,
}: TemplatePromptCardProps) {
  return (
    <Stack
      direction="row"
      data-testid="chat-template-prompt-card"
      sx={{
        alignSelf: 'flex-end',
        width: '100%',
        maxWidth: '80%',
        overflow: 'hidden',
        bgcolor: COLORS.PRIMITIVES.WHITE,
        border: `1px solid ${COLORS.GRAY[300]}`,
        borderRadius: '6px',
      }}
    >
      <Box
        aria-hidden
        sx={{
          flexShrink: 0,
          width: TEMPLATE_PROMPT_CARD_ART_SIZE.width,
          minHeight: TEMPLATE_PROMPT_CARD_ART_SIZE.height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: COLORS.GRAY[50],
          color: COLORS.GRAY[600],
        }}
      >
        {icon}
      </Box>
      <Stack
        justifyContent="center"
        gap={0.25}
        sx={{ minWidth: 0, px: 1.5, py: 1 }}
      >
        <Typography
          variant="body-xs"
          sx={{ fontWeight: 600, color: COLORS.TEXT.PRIMARY }}
        >
          {title}
        </Typography>
        <Typography
          variant="body-xs"
          sx={{
            color: COLORS.TEXT.SECONDARY,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {description}
        </Typography>
      </Stack>
    </Stack>
  );
}
